import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SLDSvgDefs } from './utils/sldTheme';
import { mapMetricsToNodeData } from './utils/metricMapper';
import { calculateLayout, LAYOUT } from './vertical/layout';
import { TotalLoadCard } from './vertical/TotalLoadCard';
import { BusInfoBar } from './vertical/BusInfoBar';
import { GeneratorCard } from './vertical/GeneratorCard';
import { BESSCard } from './vertical/BESSCard';
import { BranchLine, type BreakerState, type FlowDirection } from './vertical/BranchLine';
import { MeterIndicator } from './vertical/MeterIndicator';
import type { SLDConfig, SLDTelemetryPayload, SLDSelectedNode, SLDNodeType } from './utils/sldTypes';
import { computeDeviceState, coerceBool, type DeviceStateInput } from './utils/deviceState';
import { useDeviceAlarmSeverity } from './hooks/useDeviceAlarmSeverity';
import type { DeviceAlarmSeverity } from './utils/deviceAlarmSeverity';
import type { SLDDeviceState } from './utils/sldTypes';

// CSS for flow dot animations + pulse
const VERTICAL_SLD_CSS = `
@keyframes sld-dot-flow {
  0% { transform: translateX(0); opacity: 0; }
  10% { opacity: 1; }
  85% { opacity: 1; }
  100% { transform: translateX(var(--flow-distance)); opacity: 0; }
}
.sld-dot-flow {
  animation: sld-dot-flow 2s linear infinite;
}
@keyframes sld-pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}
.sld-pulse {
  animation: sld-pulse 1.5s ease-in-out infinite;
}
`;

interface VerticalSLDProps {
  /**
   * Site whose ACTIVE alarms drive the cards' fault/warning badges
   * (djin-cloud#684). Optional so the pre-#684 two-prop call shape still
   * type-checks; omitted means no badge is ever raised, which is the safe
   * direction — a red card can never be painted from a stale flag.
   */
  siteId?: string;
  config: SLDConfig;
  telemetry: SLDTelemetryPayload | null;
  onNodeSelect: (node: SLDSelectedNode | null) => void;
}

interface DeviceInfo {
  id: string;
  nodeType: SLDNodeType;
  data: Record<string, unknown>;
}

export default function VerticalSLD({ siteId, config, telemetry, onNodeSelect }: VerticalSLDProps) {
  // Device/block id → highest ACTIVE (uncleared AND unacked) alarm severity.
  // Shares one React Query cache entry with the detail panel's
  // useDeviceConditions, so diagram and panel can never disagree. #684.
  const alarmSeverityMap = useDeviceAlarmSeverity(siteId);
  // Inject CSS once
  useEffect(() => {
    const styleId = 'vertical-sld-css';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = VERTICAL_SLD_CSS;
      document.head.appendChild(style);
    }
  }, []);

  // Tick at 1/6 of DEVICE_NO_COMMS_THRESHOLD_MS so a tile flips into
  // no_comms within ~10s of crossing the 60s threshold (worst-case
  // observed staleness: 70s).
  const NOW_TICK_INTERVAL_MS = 10_000;
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), NOW_TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Extract and sort devices from config
  const { generators, bess, meters, breakerMap } = useMemo(() => {
    const gens: DeviceInfo[] = [];
    const bs: DeviceInfo[] = [];
    const ms: DeviceInfo[] = [];

    for (const node of config.nodes) {
      const info: DeviceInfo = { id: node.id, nodeType: node.type as SLDNodeType, data: { ...node.data as Record<string, unknown> } };
      if (node.type === 'generator') gens.push(info);
      else if (node.type === 'bess') bs.push(info);
      else if (node.type === 'meter') ms.push(info);
    }

    gens.sort((a, b) => String(a.data.label ?? '').localeCompare(String(b.data.label ?? '')));
    bs.sort((a, b) => String(a.data.label ?? '').localeCompare(String(b.data.label ?? '')));

    // Build breaker map: nodeId → breakerDeviceId
    const bMap = new Map<string, string>();
    for (const edge of config.edges) {
      const edgeData = edge.data as Record<string, unknown> | undefined;
      const breaker = edgeData?.breaker as Record<string, unknown> | undefined;
      if (breaker?.deviceId) {
        bMap.set(edge.source, breaker.deviceId as string);
        bMap.set(edge.target, breaker.deviceId as string);
      }
    }

    return { generators: gens, bess: bs, meters: ms, breakerMap: bMap };
  }, [config]);

  // Merge telemetry into device data
  const liveDevices = useMemo(() => {
    const result = new Map<string, Record<string, unknown>>();

    // Start with base data from config. `_deviceType` is a synthetic key
    // (mirrors how `deviceState` is also synthetically attached at the end
    // of this useMemo) that lets `computeDeviceState` gate type-specific
    // running predicates without each card needing to plumb its own type.
    for (const dev of [...generators, ...bess, ...meters]) {
      const deviceId = dev.data.deviceId as string;
      if (!deviceId) continue;
      result.set(deviceId, { ...dev.data, _deviceType: dev.nodeType });
    }

    // Overlay live telemetry
    if (telemetry?.devices) {
      for (const [deviceId, metrics] of Object.entries(telemetry.devices)) {
        const base = result.get(deviceId) ?? {};
        result.set(deviceId, mapMetricsToNodeData(metrics, base));
      }
    }

    // After merge, derive deviceState once per device — single source of truth
    // consumed by cards, branch lines, and breaker fall-through. Trust the
    // backend's explicit `commsOk` signal only; computeDeviceState's
    // lastSeenAt-vs-DEVICE_NO_COMMS_THRESHOLD_MS check absorbs the
    // backend cold-start case (devices absent from the post-restart batch
    // render `unknown`/`no_comms` per the freshness ladder, not red OFFLINE).
    // Issue: djin-cloud#206.
    for (const [deviceId, data] of result) {
      // ACTIVE-alarm severity for this node, resolved on the node's own
      // telemetry join key (which for a BESS tile is a block id — the map is
      // indexed under both device and block identities). #684.
      const alarmSeverity: DeviceAlarmSeverity | null =
        alarmSeverityMap.get(deviceId) ?? null;

      const deviceState: SLDDeviceState = computeDeviceState(
        {
          // The FAULT tier requires a CONFIRMING active critical alarm. Without
          // one, every fault signal is suppressed: genieos keeps `faulted` true
          // for an acked-and-open alarm, which pinned Hondo's BESS card red with
          // zero unacked alarms, and it carries no severity, which rendered
          // Turner's single active warning as full-red FAULT. #684.
          faultSuppressed: alarmSeverity !== 'critical',
          faulted: data.faulted as boolean | null | undefined,
          status: (data.status ?? data.blockStatus) as string | null | undefined,
          commsOk: data.commsOk as boolean | null | undefined,
          lastSeenAt: data.lastSeenAt as string | null | undefined,
          mode: (data.mode ?? data.blockMode ?? data.state) as string | null | undefined,
          breakerClosed: data.breakerClosed as DeviceStateInput['breakerClosed'],
          activePowerKw:
            (data.generatorPowerKw ?? data.activePowerKw) as number | null | undefined,
          deviceType: data._deviceType as SLDNodeType | undefined,
        },
        now,
      );
      result.set(deviceId, { ...data, deviceState, alarmSeverity });
    }

    return result;
  }, [generators, bess, meters, telemetry, now, alarmSeverityMap]);

  // Helper to get live data for a device
  const getLive = useCallback((dev: DeviceInfo): Record<string, unknown> => {
    const deviceId = dev.data.deviceId as string;
    return liveDevices.get(deviceId) ?? dev.data;
  }, [liveDevices]);

  // Calculate layout
  const layout = useMemo(
    () => calculateLayout(generators.length, bess.length, meters.length),
    [generators.length, bess.length, meters.length],
  );

  // Aggregated values for Total Load
  const { totalKw, feederCount, busVoltageV, busFrequencyHz, busCurrentA } = useMemo(() => {
    let total = 0;
    let feeders = 0;

    for (const gen of generators) {
      const d = getLive(gen);
      const power = Number(d.activePowerKw ?? 0);
      if (power > 0) {
        total += power;
        feeders++;
      }
    }
    for (const b of bess) {
      const d = getLive(b);
      const power = Number(d.activePowerKw ?? 0);
      // Discharging BESS contributes positive power
      if (power > 0) {
        total += power;
        feeders++;
      } else if (power < 0) {
        // Charging draws power but still a feeder
        feeders++;
      }
    }

    // Bus info from meter (if available)
    let vV: number | null = null;
    let fHz: number | null = null;
    let aA: number | null = null;

    if (meters.length > 0) {
      const m = getLive(meters[0]);
      const v = Number(m.voltageV ?? m.avgLineVoltageV ?? 0);
      if (v > 0) vV = v;
      const f = Number(m.frequencyHz ?? 0);
      if (f > 0) fHz = f;
      let a = Number(m.currentA ?? m.avgCurrentA ?? 0);
      if (!(a > 0)) {
        const phases = [m.currentPhaseA, m.currentPhaseB, m.currentPhaseC]
          .map(x => (x != null ? Number(x) : NaN))
          .filter(x => Number.isFinite(x));
        if (phases.length > 0) a = phases.reduce((s, x) => s + x, 0) / phases.length;
      }
      if (a > 0) aA = a;
    }

    return { totalKw: total, feederCount: feeders, busVoltageV: vV, busFrequencyHz: fHz, busCurrentA: aA };
  }, [generators, bess, meters, getLive]);

  // Click handlers
  const handleNodeClick = useCallback((dev: DeviceInfo) => {
    onNodeSelect({
      nodeId: dev.id,
      nodeType: dev.nodeType,
      data: getLive(dev),
    });
  }, [onNodeSelect, getLive]);

  const handleBgClick = useCallback(() => {
    onNodeSelect(null);
  }, [onNodeSelect]);

  // Determine breaker state for a device
  const getBreakerState = useCallback((dev: DeviceInfo): BreakerState => {
    const breakerDeviceId = breakerMap.get(dev.id);

    // Check the device's own breaker_closed flag from telemetry
    const d = getLive(dev);
    if (d.breakerClosed != null) {
      return d.breakerClosed === true || d.breakerClosed === 1 || d.breakerClosed === 'true'
        ? 'closed' : 'open';
    }

    // Check the linked breaker device telemetry
    if (breakerDeviceId && telemetry?.devices?.[breakerDeviceId]) {
      const bd = telemetry.devices[breakerDeviceId];
      if (bd.state === 'closed') return 'closed';
      if (bd.state === 'open') return 'open';
      if (bd.state === 'tripped') return 'tripped';
    }

    // Fall-through derived from deviceState (replaces previous status-string check)
    const state = d.deviceState as SLDDeviceState | undefined;
    if (state === 'fault') return 'tripped';
    if (state === 'running') return 'closed';
    return 'open';
  }, [breakerMap, getLive, telemetry]);

  // Determine generator flow
  const getGenFlow = useCallback((dev: DeviceInfo): FlowDirection => {
    const d = getLive(dev);
    const power = Number(d.activePowerKw ?? 0);
    return power > 0 ? 'towardBus' : 'none';
  }, [getLive]);

  // Determine gen branch color
  const getGenColor = useCallback((dev: DeviceInfo): string => {
    const d = getLive(dev);
    const state = d.deviceState as SLDDeviceState | undefined;
    if (state === 'fault') return '#fb7185';
    if (state === 'offline' || state === 'no_comms' || state === 'standby' || state === 'unknown') return '#475569';
    // Amber sits deliberately between green and red: the branch IS energised
    // (so it must not read as de-energised grey) but the asset is impaired
    // (so it must not read as healthy green). djin-cloud#618.
    if (state === 'degraded') return '#fbbf24';
    if (state === 'running') return '#34d399';
    return '#475569';
  }, [getLive]);

  // Determine BESS flow
  const getBessFlow = useCallback((dev: DeviceInfo): FlowDirection => {
    const d = getLive(dev);
    const power = Number(d.activePowerKw ?? 0);
    if (power < -1) return 'awayFromBus';   // charging: power flows from bus to BESS
    if (power > 1) return 'towardBus';       // discharging: power flows from BESS to bus
    return 'none';
  }, [getLive]);

  // Determine BESS branch color
  const getBessColor = useCallback((dev: DeviceInfo): string => {
    const d = getLive(dev);
    const state = d.deviceState as SLDDeviceState | undefined;
    if (state === 'fault') return '#fb7185';
    if (state == null || state === 'offline' || state === 'no_comms' || state === 'standby' || state === 'unknown') return '#475569';
    // Impairment outranks charge/discharge colour here (#618). Direction is not
    // lost — getBessFlow encodes it independently as animated flow direction —
    // whereas an amber-vs-blue/orange distinction is the ONLY carrier of
    // "this asset is impaired" on the branch.
    if (state === 'degraded') return '#fbbf24';
    // running tier — flex on mode for charging/discharging color
    const rawMode = String(d.mode ?? d.blockMode ?? d.state ?? d.blockStatus ?? d.status ?? '').toLowerCase();
    const power = Number(d.activePowerKw ?? 0);
    const isCharging = rawMode === 'charging' || rawMode === 'charge' || (power < -1);
    const isDischarging = rawMode === 'discharging' || rawMode === 'discharge' || (power > 1);
    if (isCharging) return '#38bdf8';
    if (isDischarging) return '#f97316';
    return '#475569';
  }, [getLive]);

  // Scroll container ref
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-auto"
      style={{ background: '#080c16' }}
    >
      <SLDSvgDefs />
      <svg
        width="100%"
        viewBox={`0 0 ${layout.canvasW} ${layout.canvasH}`}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block', maxWidth: 700, margin: '0 auto' }}
      >
        {/* Background click target */}
        <rect
          x={0} y={0} width={layout.canvasW} height={layout.canvasH}
          fill="transparent"
          onClick={handleBgClick}
        />

        {/* Total Load Card */}
        <TotalLoadCard
          x={layout.loadCard.x}
          y={layout.loadCard.y}
          totalKw={totalKw}
          feederCount={feederCount}
        />

        {/* Connector line: Total Load → Bus Info */}
        <line
          x1={layout.centerX} y1={layout.loadCard.y + LAYOUT.loadCardH}
          x2={layout.centerX} y2={layout.busInfo.y}
          stroke="#2563eb" strokeWidth={3}
        />

        {/* Bus Info Bar */}
        <BusInfoBar
          x={layout.busInfo.x}
          y={layout.busInfo.y}
          voltageV={busVoltageV}
          frequencyHz={busFrequencyHz}
          currentA={busCurrentA}
        />

        {/* Connector line: Bus Info → Vertical Bus */}
        <line
          x1={layout.centerX} y1={layout.busInfo.y + LAYOUT.busInfoH}
          x2={layout.centerX} y2={layout.busTop}
          stroke="#2563eb" strokeWidth={3}
        />

        {/* Vertical Bus Bar */}
        <rect
          x={layout.centerX - LAYOUT.busWidth / 2}
          y={layout.busTop}
          width={LAYOUT.busWidth}
          height={layout.busBottom - layout.busTop}
          rx={3}
          fill="#1e3a8a"
          stroke="#3b82f6"
          strokeWidth={1.5}
        />
        {/* Bus inner highlight */}
        <rect
          x={layout.centerX - 2}
          y={layout.busTop + 2}
          width={4}
          height={layout.busBottom - layout.busTop - 4}
          rx={2}
          fill="#3b82f6"
          opacity={0.4}
        />

        {/* Meter Indicators on Bus */}
        {meters.map((meterDev, i) => {
          if (i >= layout.meters.length) return null;
          const pos = layout.meters[i];
          const d = getLive(meterDev);
          return (
            <MeterIndicator
              key={meterDev.id}
              cx={pos.cx}
              cy={pos.cy}
              label={String(d.label ?? '')}
              activePowerKw={d.activePowerKw != null ? Number(d.activePowerKw) : null}
              status={String(d.status ?? '')}
              deviceState={d.deviceState as SLDDeviceState | undefined}
              onClick={() => handleNodeClick(meterDev)}
            />
          );
        })}

        {/* Device Rows */}
        {layout.rows.map((row, i) => {
          const gen = i < generators.length ? generators[i] : null;
          const bes = i < bess.length ? bess[i] : null;

          return (
            <g key={i}>
              {/* Generator (left side) */}
              {gen && (() => {
                const d = getLive(gen);
                return (
                  <>
                    <BranchLine
                      startX={row.genBranchStart}
                      endX={row.genBranchEnd}
                      y={row.y}
                      breakerState={getBreakerState(gen)}
                      flowDirection={getGenFlow(gen)}
                      color={getGenColor(gen)}
                      side="left"
                    />
                    <GeneratorCard
                      x={row.genCardX}
                      y={row.y}
                      label={String(d.label ?? '')}
                      activePowerKw={
                        d.generatorPowerKw != null
                          ? Number(d.generatorPowerKw)
                          : (d.activePowerKw != null ? Number(d.activePowerKw) : null)
                      }
                      engineLoadPct={d.engineLoadPct != null ? Number(d.engineLoadPct) : null}
                      status={String(d.status ?? d.blockStatus ?? '')}
                      faulted={coerceBool(d.faulted)}
                      alarmSeverity={d.alarmSeverity as DeviceAlarmSeverity | null | undefined}
                      commsOk={d.commsOk === false ? false : (d.commsOk as boolean | undefined)}
                      deviceState={d.deviceState as SLDDeviceState | undefined}
                      breakerClosed={
                        d.breakerClosed === true || d.breakerClosed === 'true'
                          ? true
                          : d.breakerClosed === false || d.breakerClosed === 'false'
                          ? false
                          : undefined
                      }
                      onClick={() => handleNodeClick(gen)}
                    />
                  </>
                );
              })()}

              {/* BESS (right side) */}
              {bes && (() => {
                const d = getLive(bes);
                return (
                  <>
                    <BranchLine
                      startX={row.bessBranchEnd}
                      endX={row.bessBranchStart}
                      y={row.y}
                      breakerState={getBreakerState(bes)}
                      flowDirection={getBessFlow(bes)}
                      color={getBessColor(bes)}
                      side="right"
                    />
                    <BESSCard
                      x={row.bessCardX}
                      y={row.y}
                      label={String(d.label ?? '')}
                      activePowerKw={d.activePowerKw != null ? Number(d.activePowerKw) : null}
                      stateOfCharge={d.stateOfCharge != null ? Number(d.stateOfCharge) : null}
                      mode={String(d.mode ?? d.blockMode ?? d.state ?? d.blockStatus ?? d.status ?? '')}
                      status={String(d.status ?? '')}
                      alarmSeverity={d.alarmSeverity as DeviceAlarmSeverity | null | undefined}
                      deviceState={d.deviceState as SLDDeviceState | undefined}
                      onClick={() => handleNodeClick(bes)}
                    />
                  </>
                );
              })()}
            </g>
          );
        })}
      </svg>

      {/* Telemetry timestamp */}
      {telemetry?.timestamp && (
        <div className="text-center pb-4">
          <span className="text-[10px] text-slate-600 font-mono bg-slate-950/80 px-2 py-1 rounded">
            Updated: {new Date(telemetry.timestamp).toLocaleTimeString()}
          </span>
        </div>
      )}
    </div>
  );
}
